
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SaveIcon, RefreshCw } from "lucide-react";

const SystemSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState({
    general: {
      siteName: "AssessPro",
      siteDescription: "Online Assessment Platform",
      maintenanceMode: false,
      allowSignups: true,
      defaultPlan: "basic",
      trialDays: 15,
    },
    email: {
      smtpServer: "",
      smtpPort: "",
      smtpUsername: "",
      smtpPassword: "",
      senderEmail: "noreply@assesspro.com",
      senderName: "AssessPro",
      enableEmailNotifications: true,
    },
    security: {
      passwordMinLength: 8,
      requirePasswordReset: 90, // days
      maxLoginAttempts: 5,
      blockDuration: 15, // minutes
      enableTwoFactorAuth: true,
      allowRememberMe: true,
    },
    payment: {
      stripeEnabled: true,
      stripeTestMode: true,
      razorpayEnabled: true,
      razorpayTestMode: true,
      paypalEnabled: true,
      paypalTestMode: true,
      currency: "USD",
      taxRate: 0,
    },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get("/api/settings/system");
        setSettings(response.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
        toast.error("Failed to load system settings");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSaveSettings = async (category) => {
    try {
      setIsLoading(true);
      await axios.put(`/api/settings/system/${category}`, settings[category]);
      toast.success(`${category.charAt(0).toUpperCase() + category.slice(1)} settings saved successfully`);
    } catch (error) {
      console.error(`Error saving ${category} settings:`, error);
      toast.error(`Failed to save ${category} settings`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (category, field, value) => {
    setSettings({
      ...settings,
      [category]: {
        ...settings[category],
        [field]: value,
      },
    });
  };

  return (
    <div className="container py-10">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">System Settings</h1>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="email">Email</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="payment">Payment</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>
                Manage general system settings for your assessment platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="siteName">Site Name</Label>
                <Input
                  id="siteName"
                  value={settings.general.siteName}
                  onChange={(e) =>
                    handleInputChange("general", "siteName", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="siteDescription">Site Description</Label>
                <Input
                  id="siteDescription"
                  value={settings.general.siteDescription}
                  onChange={(e) =>
                    handleInputChange(
                      "general",
                      "siteDescription",
                      e.target.value
                    )
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Maintenance Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable maintenance mode to temporarily disable access to the
                    platform
                  </p>
                </div>
                <Switch
                  checked={settings.general.maintenanceMode}
                  onCheckedChange={(checked) =>
                    handleInputChange("general", "maintenanceMode", checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Allow New Signups</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow new companies to register on the platform
                  </p>
                </div>
                <Switch
                  checked={settings.general.allowSignups}
                  onCheckedChange={(checked) =>
                    handleInputChange("general", "allowSignups", checked)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="defaultPlan">Default Plan</Label>
                <select
                  id="defaultPlan"
                  value={settings.general.defaultPlan}
                  onChange={(e) =>
                    handleInputChange("general", "defaultPlan", e.target.value)
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                  <option value="free_trial">Free Trial</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="trialDays">Free Trial Days</Label>
                <Input
                  id="trialDays"
                  type="number"
                  value={settings.general.trialDays}
                  onChange={(e) =>
                    handleInputChange(
                      "general",
                      "trialDays",
                      parseInt(e.target.value) || 0
                    )
                  }
                />
              </div>

              <Button
                onClick={() => handleSaveSettings("general")}
                disabled={isLoading}
                className="mt-4"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <SaveIcon className="mr-2 h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>Email Settings</CardTitle>
              <CardDescription>
                Configure email server settings and notification preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="smtpServer">SMTP Server</Label>
                <Input
                  id="smtpServer"
                  value={settings.email.smtpServer}
                  onChange={(e) =>
                    handleInputChange("email", "smtpServer", e.target.value)
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtpPort">SMTP Port</Label>
                  <Input
                    id="smtpPort"
                    value={settings.email.smtpPort}
                    onChange={(e) =>
                      handleInputChange("email", "smtpPort", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtpUsername">SMTP Username</Label>
                  <Input
                    id="smtpUsername"
                    value={settings.email.smtpUsername}
                    onChange={(e) =>
                      handleInputChange("email", "smtpUsername", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtpPassword">SMTP Password</Label>
                <Input
                  id="smtpPassword"
                  type="password"
                  value={settings.email.smtpPassword}
                  onChange={(e) =>
                    handleInputChange("email", "smtpPassword", e.target.value)
                  }
                  placeholder="••••••••••••"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="senderEmail">Sender Email</Label>
                  <Input
                    id="senderEmail"
                    value={settings.email.senderEmail}
                    onChange={(e) =>
                      handleInputChange("email", "senderEmail", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="senderName">Sender Name</Label>
                  <Input
                    id="senderName"
                    value={settings.email.senderName}
                    onChange={(e) =>
                      handleInputChange("email", "senderName", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">
                    Enable Email Notifications
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Send email notifications for important system events
                  </p>
                </div>
                <Switch
                  checked={settings.email.enableEmailNotifications}
                  onCheckedChange={(checked) =>
                    handleInputChange(
                      "email",
                      "enableEmailNotifications",
                      checked
                    )
                  }
                />
              </div>

              <Button
                onClick={() => handleSaveSettings("email")}
                disabled={isLoading}
                className="mt-4"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <SaveIcon className="mr-2 h-4 w-4" />
                    Save Email Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Configure security settings for your assessment platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="passwordMinLength">
                  Minimum Password Length
                </Label>
                <Input
                  id="passwordMinLength"
                  type="number"
                  value={settings.security.passwordMinLength}
                  onChange={(e) =>
                    handleInputChange(
                      "security",
                      "passwordMinLength",
                      parseInt(e.target.value) || 0
                    )
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requirePasswordReset">
                  Password Reset Interval (days)
                </Label>
                <Input
                  id="requirePasswordReset"
                  type="number"
                  value={settings.security.requirePasswordReset}
                  onChange={(e) =>
                    handleInputChange(
                      "security",
                      "requirePasswordReset",
                      parseInt(e.target.value) || 0
                    )
                  }
                />
                <p className="text-sm text-muted-foreground">
                  Set to 0 to disable password reset requirement
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="maxLoginAttempts">Max Login Attempts</Label>
                  <Input
                    id="maxLoginAttempts"
                    type="number"
                    value={settings.security.maxLoginAttempts}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "maxLoginAttempts",
                        parseInt(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="blockDuration">
                    Block Duration (minutes)
                  </Label>
                  <Input
                    id="blockDuration"
                    type="number"
                    value={settings.security.blockDuration}
                    onChange={(e) =>
                      handleInputChange(
                        "security",
                        "blockDuration",
                        parseInt(e.target.value) || 0
                      )
                    }
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Enable Two-Factor Auth</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow users to enable two-factor authentication
                  </p>
                </div>
                <Switch
                  checked={settings.security.enableTwoFactorAuth}
                  onCheckedChange={(checked) =>
                    handleInputChange(
                      "security",
                      "enableTwoFactorAuth",
                      checked
                    )
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">"Remember Me" Option</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow users to stay logged in
                  </p>
                </div>
                <Switch
                  checked={settings.security.allowRememberMe}
                  onCheckedChange={(checked) =>
                    handleInputChange("security", "allowRememberMe", checked)
                  }
                />
              </div>

              <Button
                onClick={() => handleSaveSettings("security")}
                disabled={isLoading}
                className="mt-4"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <SaveIcon className="mr-2 h-4 w-4" />
                    Save Security Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payment">
          <Card>
            <CardHeader>
              <CardTitle>Payment Settings</CardTitle>
              <CardDescription>
                Configure payment gateways and billing settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label className="text-base">Stripe</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable Stripe payment gateway
                    </p>
                  </div>
                  <Switch
                    checked={settings.payment.stripeEnabled}
                    onCheckedChange={(checked) =>
                      handleInputChange("payment", "stripeEnabled", checked)
                    }
                  />
                </div>

                {settings.payment.stripeEnabled && (
                  <div className="ml-6 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={settings.payment.stripeTestMode}
                        onCheckedChange={(checked) =>
                          handleInputChange(
                            "payment",
                            "stripeTestMode",
                            checked
                          )
                        }
                        id="stripe-test-mode"
                      />
                      <Label htmlFor="stripe-test-mode">Test Mode</Label>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label className="text-base">Razorpay</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable Razorpay payment gateway (India)
                    </p>
                  </div>
                  <Switch
                    checked={settings.payment.razorpayEnabled}
                    onCheckedChange={(checked) =>
                      handleInputChange("payment", "razorpayEnabled", checked)
                    }
                  />
                </div>

                {settings.payment.razorpayEnabled && (
                  <div className="ml-6 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={settings.payment.razorpayTestMode}
                        onCheckedChange={(checked) =>
                          handleInputChange(
                            "payment",
                            "razorpayTestMode",
                            checked
                          )
                        }
                        id="razorpay-test-mode"
                      />
                      <Label htmlFor="razorpay-test-mode">Test Mode</Label>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label className="text-base">PayPal</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable PayPal payment gateway
                    </p>
                  </div>
                  <Switch
                    checked={settings.payment.paypalEnabled}
                    onCheckedChange={(checked) =>
                      handleInputChange("payment", "paypalEnabled", checked)
                    }
                  />
                </div>

                {settings.payment.paypalEnabled && (
                  <div className="ml-6 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={settings.payment.paypalTestMode}
                        onCheckedChange={(checked) =>
                          handleInputChange(
                            "payment",
                            "paypalTestMode",
                            checked
                          )
                        }
                        id="paypal-test-mode"
                      />
                      <Label htmlFor="paypal-test-mode">Test Mode</Label>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currency">Default Currency</Label>
                  <select
                    id="currency"
                    value={settings.payment.currency}
                    onChange={(e) =>
                      handleInputChange("payment", "currency", e.target.value)
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="taxRate">Tax Rate (%)</Label>
                  <Input
                    id="taxRate"
                    type="number"
                    value={settings.payment.taxRate}
                    onChange={(e) =>
                      handleInputChange(
                        "payment",
                        "taxRate",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
              </div>

              <Button
                onClick={() => handleSaveSettings("payment")}
                disabled={isLoading}
                className="mt-4"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <SaveIcon className="mr-2 h-4 w-4" />
                    Save Payment Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SystemSettings;
